import type { WasmWorkerRequest, WorkerResponse } from './types'

interface PendingRequest {
  resolve: (data: ArrayBuffer | null) => void
  reject: (error: Error) => void
}

/**
 * Lazy FFmpeg client. The worker (and the ~32MB WASM core) is only created on
 * the first call — entering the video/audio tool page. The worker is reused
 * across conversions and can be disposed to free memory.
 */
let worker: Worker | null = null
let nextId = 1
const pending = new Map<number, PendingRequest>()
// The conversion currently being run; receives progress broadcasts.
let active: { id: number; onProgress?: (progress: number) => void } | null = null

function getWorker(): Worker {
  if (worker) return worker

  const instance = new Worker(new URL('./ffmpeg-worker.ts', import.meta.url), { type: 'module' })

  instance.onmessage = (event: MessageEvent<WorkerResponse>) => {
    const message = event.data

    if (message.type === 'progress') {
      active?.onProgress?.(message.progress ?? 0)
      return
    }

    const entry = pending.get(message.id)
    if (!entry) return
    pending.delete(message.id)

    if (message.type === 'result') {
      entry.resolve(message.data ?? null)
    } else if (message.type === 'error') {
      entry.reject(new Error(message.message ?? 'FFmpeg conversion failed.'))
    }
  }

  instance.onerror = (event) => {
    // Worker crashed: reject everything and reset so the next call retries.
    for (const entry of pending.values()) {
      entry.reject(new Error('The processing worker crashed. Please try again.'))
    }
    pending.clear()
    active = null
    instance.terminate()
    worker = null
    void event
  }

  worker = instance
  return instance
}

function post(request: WasmWorkerRequest, transfer?: Transferable[]): void {
  getWorker().postMessage(request, { transfer })
}

/** Initialize the WASM core. Resolves when FFmpeg is ready for work. */
export function loadFfmpeg(): Promise<void> {
  const id = nextId
  nextId += 1

  return new Promise<void>((resolve, reject) => {
    pending.set(id, {
      resolve: () => resolve(),
      reject: (error) => reject(error),
    })
    post({ id, type: 'load' })
  })
}

export interface ExecOptions {
  args: string[]
  input?: { name: string; data: ArrayBuffer }
  outputName?: string
  onProgress?: (progress: number) => void
}

/** Run an ffmpeg command in the worker; returns the output bytes. */
export function execFfmpeg(options: ExecOptions): Promise<ArrayBuffer | null> {
  const id = nextId
  nextId += 1
  active = { id, onProgress: options.onProgress }

  return new Promise<ArrayBuffer | null>((resolve, reject) => {
    pending.set(id, {
      resolve: (data) => {
        if (active?.id === id) active = null
        resolve(data)
      },
      reject: (error) => {
        if (active?.id === id) active = null
        reject(error)
      },
    })

    post(
      {
        id,
        type: 'exec',
        args: options.args,
        input: options.input,
        outputName: options.outputName,
      },
      options.input ? [options.input.data] : [],
    )
  })
}

/** Terminate the worker and release all WASM memory. */
export function disposeFfmpeg(): void {
  worker?.terminate()
  worker = null
  pending.clear()
  active = null
}
